/* import __COLOCATED_TEMPLATE__ from './trial-info-modal.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type FinTrialService from 'embercom/services/fin-trial-service';
import type AppService from 'embercom/services/app-service';
import { AiAnswersState, selectedAgentToTrialType } from 'embercom/lib/operator/fin/types';
import { AGENT_OPTIONS } from 'embercom/lib/operator/resolution-bot/constants';
import moment from 'moment-timezone';

interface Args {
  selectedAgentValue: string;
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

export default class TrialInfoModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finTrialService: FinTrialService;
  @service declare appService: AppService;

  private get agentTrialType() {
    return selectedAgentToTrialType(this.args.selectedAgentValue);
  }

  private get agentState() {
    return this.finTrialService.getAgentTrialState(this.agentTrialType);
  }

  get isTrialStarted(): boolean {
    return this.agentState?.state === AiAnswersState.trialStarted;
  }

  get isSalesAgent(): boolean {
    return this.args.selectedAgentValue === AGENT_OPTIONS.FIN_SDR;
  }

  get title(): string {
    if (this.isTrialStarted) {
      return this.intl.t(
        'operator.workflows.visual-builder.pass-to-fin.settings.trial-dialog.started-title',
        { remainingDays: this.remainingDays },
      );
    }
    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.trial-dialog.not-started-title',
    );
  }

  get remainingDays(): number {
    if (this.agentState?.trialDetails?.endDate) {
      return moment(this.agentState.trialDetails.endDate).diff(moment(), 'days');
    }
    return 0;
  }

  get agentDisplayName(): string {
    if (this.args.selectedAgentValue === AGENT_OPTIONS.FIN_SDR) {
      return this.intl.t('operator.workflows.visual-builder.fin.agents.sales-agent');
    } else if (this.args.selectedAgentValue === AGENT_OPTIONS.FIN_ECOMMERCE) {
      return this.intl.t('operator.workflows.visual-builder.fin.agents.ecommerce-agent');
    } else if (this.args.selectedAgentValue === AGENT_OPTIONS.FIN_SUCCESS) {
      return this.intl.t('operator.workflows.visual-builder.fin.agents.customer-success-agent');
    }
    return this.intl.t('operator.workflows.visual-builder.fin.agents.service-agent');
  }

  get pricingText(): string {
    if (this.isSalesAgent) {
      return this.intl.t(
        'operator.workflows.visual-builder.pass-to-fin.settings.trial-dialog.pricing-sales',
      );
    }
    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.trial-dialog.pricing-support',
    );
  }

  get closeButtonLabel(): string {
    if (this.isTrialStarted && this.isSalesAgent) {
      return this.intl.t(
        'operator.workflows.visual-builder.pass-to-fin.settings.trial-dialog.got-it',
      );
    }
    return this.intl.t('operator.workflows.visual-builder.pass-to-fin.settings.trial-dialog.close');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::TrialInfoModal': typeof TrialInfoModal;
  }
}
